import React, { useEffect, useState } from 'react';
import nexo from '../../nexoClient';
import { DataList, DataTable, Page } from '@nimbus-ds/patterns';
import {
  Box,
  Button,
  Card,
  Icon,
  Input,
  Modal,
  Select,
  Table,
  Tag,
  Text,
  useToast,
} from '@nimbus-ds/components';
import { navigateHeader } from '@tiendanube/nexo/helpers';
import axiosInstance from '../../axios';
import {
  BoxUnpackedIcon,
  RedoIcon,
  ShoppingCartIcon,
  UserIcon,
} from '@nimbus-ds/icons';
import { useHistory } from 'react-router-dom';

type LogResource = 'products' | 'orders' | 'customers';
type LogTypeFilter = 'ALL' | 'EXITO' | 'ERROR' | 'INFO';
type DatePreset = 'CUSTOM' | 'TODAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS';
type LogCellValue = string | number | null | undefined;

interface MobileLogField {
  label: string;
  value: LogCellValue;
}

type RetryUnavailableReason =
  | 'AUTO_RETRY_PENDING'
  | 'ALREADY_PROCESSING'
  | 'ALREADY_SENT'
  | 'MAX_MANUAL_RETRIES'
  | 'NOT_LATEST_LOG'
  | 'NOT_RETRYABLE_STATUS';

interface InvoicingRetry {
  queue_id: number;
  status: string;
  last_error_code: string | null;
  can_retry: boolean;
  unavailable_reason: RetryUnavailableReason | null;
  manual_retry_count: number;
  manual_retry_limit: number;
  manual_retry_remaining: number;
  is_latest_order_log: boolean;
  next_attempt_at: string | null;
  feedback_message: string | null;
}

interface ProductLog {
  id: number;
  sku: string | null;
  type: string | null;
  tn_id: string | number | null;
  variant_tn_id?: string | number | null;
  siigo_id: string | null;
  message: string | null;
  created_at: string | null;
}

interface CustomerLog {
  id: number;
  customer_identification: string | null;
  type: string | null;
  customer_name: string | null;
  tn_id: string | number | null;
  siigo_id: string | null;
  message: string | null;
  created_at: string | null;
}

interface OrderLog {
  id: number;
  tn_order_number: number | null;
  type: string | null;
  customer_identification: string | null;
  siigo_id: string | null;
  message: string | null;
  created_at: string | null;
  invoicing_retry?: InvoicingRetry | null;
}

interface PaginationMeta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
}

interface LogFilters {
  search: string;
  type: LogTypeFilter;
  dateFrom: string;
  dateTo: string;
  datePreset: DatePreset;
  page: number;
}

const PAGE_SIZE = 50;
const TABLE_MIN_WIDTHS: Record<LogResource, string> = {
  products: '806px',
  orders: '1032px',
  customers: '992px',
};

const defaultPagination = (): PaginationMeta => ({
  total: 0,
  per_page: PAGE_SIZE,
  current_page: 1,
  last_page: 1,
  from: 0,
  to: 0,
});

const defaultFilters = (): LogFilters => ({
  search: '',
  type: 'ALL',
  dateFrom: '',
  dateTo: '',
  datePreset: 'CUSTOM',
  page: 1,
});

const getFormattedDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const getDateParam = (date: string) => {
  const match = date.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (!match) return null;

  const [, day, month, year] = match;
  const isoDate = `${year}-${month}-${day}`;
  const parsedDate = new Date(`${isoDate}T00:00:00`);
  const isValidDate =
    !Number.isNaN(parsedDate.getTime()) &&
    parsedDate.getDate() === Number(day) &&
    parsedDate.getMonth() + 1 === Number(month) &&
    parsedDate.getFullYear() === Number(year);

  return isValidDate ? isoDate : null;
};

const getLocalDateInput = (date: Date) => {
  return getFormattedDate(date);
};

const getDatePresetRange = (preset: DatePreset) => {
  if (preset === 'CUSTOM') return { dateFrom: '', dateTo: '' };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dateFrom = new Date(today);

  if (preset === 'LAST_7_DAYS') {
    dateFrom.setDate(today.getDate() - 6);
  }

  if (preset === 'LAST_30_DAYS') {
    dateFrom.setDate(today.getDate() - 29);
  }

  return {
    dateFrom: getLocalDateInput(dateFrom),
    dateTo: getLocalDateInput(today),
  };
};

const LogsPage: React.FC = () => {
  const { addToast } = useToast();
  const { push } = useHistory();
  const [activeResource, setActiveResource] = useState<LogResource>('orders');
  const [products, setProductsLogs] = useState<ProductLog[]>([]);
  const [orders, setOrdersLogs] = useState<OrderLog[]>([]);
  const [customers, setCustomersLogs] = useState<CustomerLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [retryOrder, setRetryOrder] = useState<OrderLog | null>(null);
  const [isRetryingOrder, setIsRetryingOrder] = useState(false);
  const [filters, setFilters] = useState<Record<LogResource, LogFilters>>({
    products: defaultFilters(),
    orders: defaultFilters(),
    customers: defaultFilters(),
  });
  const [pagination, setPagination] = useState<
    Record<LogResource, PaginationMeta>
  >({
    products: defaultPagination(),
    orders: defaultPagination(),
    customers: defaultPagination(),
  });

  const goToSetup = () => push('/setup');

  const getErrorCode = (error: unknown) => {
    return (error as { response?: { data?: { error_code?: string } } })
      ?.response?.data?.error_code;
  };

  const getErrorMessage = (error: unknown) => {
    return (error as { response?: { data?: { message?: string } } })?.response
      ?.data?.message;
  };

  const getRowsFromResponse = (
    resource: LogResource,
    data: {
      logs?: ProductLog[] | OrderLog[] | CustomerLog[];
      p_logs?: ProductLog[];
      o_logs?: OrderLog[];
      c_logs?: CustomerLog[];
    },
  ) => {
    if (data.logs) return data.logs;
    if (resource === 'products') return data.p_logs ?? [];
    if (resource === 'orders') return data.o_logs ?? [];

    return data.c_logs ?? [];
  };

  const updateResourceFilters = (
    resource: LogResource,
    nextFilters: LogFilters,
  ) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [resource]: nextFilters,
    }));
  };

  const fetchLogs = async (
    resource: LogResource = activeResource,
    nextFilters: LogFilters = filters[resource],
  ) => {
    setIsLoading(true);

    const params: {
      resource: LogResource;
      page: number;
      per_page: number;
      type?: Exclude<LogTypeFilter, 'ALL'>;
      search?: string;
      date_from?: string;
      date_to?: string;
    } = {
      resource,
      page: nextFilters.page,
      per_page: PAGE_SIZE,
    };

    if (nextFilters.type !== 'ALL') {
      params.type = nextFilters.type;
    }

    if (nextFilters.search.trim()) {
      params.search = nextFilters.search.trim();
    }

    const dateFrom = getDateParam(nextFilters.dateFrom);
    const dateTo = getDateParam(nextFilters.dateTo);

    if (dateFrom) {
      params.date_from = dateFrom;
    }

    if (dateTo) {
      params.date_to = dateTo;
    }

    try {
      const response = await axiosInstance.get('/logs', { params });
      const rows = getRowsFromResponse(resource, response.data);

      if (resource === 'products') {
        setProductsLogs(rows as ProductLog[]);
      }

      if (resource === 'orders') {
        setOrdersLogs(rows as OrderLog[]);
      }

      if (resource === 'customers') {
        setCustomersLogs(rows as CustomerLog[]);
      }

      setPagination((currentPagination) => ({
        ...currentPagination,
        [resource]: response.data.pagination ?? defaultPagination(),
      }));
    } catch (error) {
      console.error(error);
      if (getErrorCode(error) === 'APP_VALIDATION_PENDING') {
        goToSetup();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderRetry = (orderId: number, retry: InvoicingRetry) => {
    setOrdersLogs((currentOrders) =>
      currentOrders.map((order) =>
        order.id === orderId ? { ...order, invoicing_retry: retry } : order,
      ),
    );
  };

  const closeRetryModal = () => {
    if (!isRetryingOrder) {
      setRetryOrder(null);
    }
  };

  const confirmRetryOrder = async () => {
    if (!retryOrder?.invoicing_retry?.queue_id) {
      return;
    }

    setIsRetryingOrder(true);

    try {
      const response = await axiosInstance.post('/invoicing/orders/retry', {
        queue_id: retryOrder.invoicing_retry.queue_id,
      });

      if (response.data?.retry) {
        updateOrderRetry(retryOrder.id, response.data.retry);
      }

      setRetryOrder(null);
      addToast({
        id: `retry-order-${retryOrder.id}-success`,
        type: 'success',
        text: 'Reintento encolado. Refresca los registros en unos minutos para ver el resultado.',
        duration: 8000,
      });
    } catch (error) {
      console.error(error);

      if (getErrorCode(error) === 'APP_VALIDATION_PENDING') {
        goToSetup();
        return;
      }

      addToast({
        id: `retry-order-${retryOrder.id}-error`,
        type: 'danger',
        text: getErrorMessage(error) || 'No se pudo encolar el reintento.',
        duration: 4000,
      });
    } finally {
      setIsRetryingOrder(false);
    }
  };

  useEffect(() => {
    navigateHeader(nexo, { goTo: '/', text: 'Volver al inicio' });
  }, []);

  useEffect(() => {
    fetchLogs('orders', filters.orders);
  }, []);

  const activeFilters = filters[activeResource];
  const activePagination = pagination[activeResource];

  const viewResource = (resource: LogResource) => {
    setActiveResource(resource);
    fetchLogs(resource, filters[resource]);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateResourceFilters(activeResource, {
      ...activeFilters,
      search: event.target.value,
      page: 1,
    });
  };

  const applyCurrentFilters = () => {
    const nextFilters = {
      ...filters[activeResource],
      page: 1,
    };

    updateResourceFilters(activeResource, nextFilters);
    fetchLogs(activeResource, nextFilters);
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      applyCurrentFilters();
    }
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextFilters = {
      ...activeFilters,
      type: event.target.value as LogTypeFilter,
      page: 1,
    };

    updateResourceFilters(activeResource, nextFilters);
    fetchLogs(activeResource, nextFilters);
  };

  const handleDatePresetChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const datePreset = event.target.value as DatePreset;
    const nextFilters = {
      ...activeFilters,
      ...getDatePresetRange(datePreset),
      datePreset,
      page: 1,
    };

    updateResourceFilters(activeResource, nextFilters);
    fetchLogs(activeResource, nextFilters);
  };

  const handleDateChange =
    (field: 'dateFrom' | 'dateTo') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextFilters = {
        ...activeFilters,
        [field]: event.target.value,
        datePreset: 'CUSTOM' as DatePreset,
        page: 1,
      };

      updateResourceFilters(activeResource, nextFilters);
    };

  const clearFilters = () => {
    const nextFilters = defaultFilters();

    updateResourceFilters(activeResource, nextFilters);
    fetchLogs(activeResource, nextFilters);
  };

  const handlePageChange = (page: number) => {
    const nextFilters = {
      ...activeFilters,
      page,
    };

    updateResourceFilters(activeResource, nextFilters);
    fetchLogs(activeResource, nextFilters);
  };

  const getResourceTitle = () => {
    if (activeResource === 'orders') return 'Logs ordenes';
    if (activeResource === 'customers') return 'Logs clientes';

    return 'Logs productos';
  };

  const getSearchPlaceholder = () => {
    if (activeResource === 'orders') return 'Buscar por orden o cliente';
    if (activeResource === 'customers') return 'Buscar por cliente';

    return 'Buscar por SKU';
  };

  const getTypeAppearance = (type: string | null) => {
    if (type === 'ERROR') return 'danger';
    if (type === 'EXITO') return 'success';
    if (type === 'INFO') return 'primary';

    return 'neutral';
  };

  const getCellValue = (value: LogCellValue) => {
    if (value === null || value === undefined || value === '') return '-';

    return value;
  };

  const renderType = (type: string | null) => (
    <Tag appearance={getTypeAppearance(type)}>
      <Text>{getCellValue(type)}</Text>
    </Tag>
  );

  const getRetryStatusLabel = (retry?: InvoicingRetry | null) => {
    if (!retry) {
      return '-';
    }

    if (retry.can_retry) {
      return null;
    }

    if (
      retry.status === 'SENT' ||
      retry.unavailable_reason === 'ALREADY_SENT'
    ) {
      return 'Facturada';
    }

    if (
      retry.status === 'PENDING_STABILIZATION' ||
      retry.status === 'ENQUEUED'
    ) {
      return 'En cola';
    }

    if (
      retry.status === 'PROCESSING' ||
      retry.unavailable_reason === 'ALREADY_PROCESSING'
    ) {
      return 'En proceso';
    }

    if (retry.unavailable_reason === 'AUTO_RETRY_PENDING') {
      return 'Reintento automatico';
    }

    if (retry.unavailable_reason === 'MAX_MANUAL_RETRIES') {
      return 'Sin intentos';
    }

    return '-';
  };

  const getRetryStatusAppearance = (retry?: InvoicingRetry | null) => {
    if (
      retry?.status === 'SENT' ||
      retry?.unavailable_reason === 'ALREADY_SENT'
    ) {
      return 'success';
    }

    if (retry?.unavailable_reason === 'MAX_MANUAL_RETRIES') {
      return 'danger';
    }

    return 'primary';
  };

  const renderOrderRetryAction = (order: OrderLog) => {
    const retry = order.invoicing_retry;

    if (retry?.can_retry) {
      return (
        <Button
          appearance="primary"
          disabled={isLoading || isRetryingOrder}
          onClick={() => setRetryOrder(order)}
        >
          Reintentar
        </Button>
      );
    }

    const statusLabel = getRetryStatusLabel(retry);

    if (!statusLabel || statusLabel === '-') {
      return <Text>-</Text>;
    }

    return (
      <Tag appearance={getRetryStatusAppearance(retry)}>
        <Text>{statusLabel}</Text>
      </Tag>
    );
  };

  const renderRetryModal = () => (
    <Modal onDismiss={closeRetryModal} open={Boolean(retryOrder)}>
      <React.Fragment key=".0">
        <Modal.Header title="Reintentar facturacion" />
        <Modal.Body padding="none">
          <Box display="flex" flexDirection="column" gap="2" p="4">
            <Text>
              Vas a reintentar la facturacion de la orden{' '}
              {getCellValue(retryOrder?.tn_order_number)}.
            </Text>
            <Text>
              Verifica que ya corregiste la causa del error antes de reintentar.
            </Text>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isRetryingOrder} onClick={closeRetryModal}>
            Cancelar
          </Button>
          <Button
            appearance="primary"
            disabled={isRetryingOrder}
            onClick={confirmRetryOrder}
          >
            Reintentar
          </Button>
        </Modal.Footer>
      </React.Fragment>
    </Modal>
  );

  const getItemCountText = () => {
    if (activePagination.total === 0) {
      return 'Mostrando 0 registros';
    }

    return `Mostrando ${activePagination.from}-${activePagination.to} de ${activePagination.total} registros`;
  };

  const renderEmptyRow = (cellCount: number) => (
    <Table.Row>
      {Array.from({ length: cellCount }, (_, index) => (
        <Table.Cell key={index}>
          {index === 0 ? <Text>Sin registros para mostrar</Text> : ''}
        </Table.Cell>
      ))}
    </Table.Row>
  );

  const renderMobileEmptyRow = () => (
    <DataList.Row>
      <Text>Sin registros para mostrar</Text>
    </DataList.Row>
  );

  const renderMobileRow = (
    key: number,
    title: string,
    type: string | null,
    fields: MobileLogField[],
    action?: React.ReactNode,
  ) => (
    <DataList.Row key={key} gap="2">
      <Box
        alignItems="center"
        display="flex"
        gap="2"
        justifyContent="space-between"
      >
        <Text fontWeight="medium">{title}</Text>
        {renderType(type)}
      </Box>
      {fields.map((field) => (
        <Box alignItems="flex-start" display="flex" gap="1" key={field.label}>
          <Text fontSize="caption" fontWeight="medium">
            {field.label}:
          </Text>
          <Text fontSize="caption">{getCellValue(field.value)}</Text>
        </Box>
      ))}
      {action && (
        <Box display="flex" justifyContent="flex-end">
          {action}
        </Box>
      )}
    </DataList.Row>
  );

  const renderProductsHeader = () => (
    <Table.Head>
      <Table.Row backgroundColor="neutral-surface">
        <Table.Cell as="th" width="80px">
          SKU
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          TIPO
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          TN ID
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          SIIGO ID
        </Table.Cell>
        <Table.Cell as="th" width="240px">
          MENSAJE
        </Table.Cell>
        <Table.Cell as="th" width="150px">
          FECHA CREACION
        </Table.Cell>
      </Table.Row>
    </Table.Head>
  );

  const renderProductsRows = () => {
    if (!products.length) return renderEmptyRow(6);

    return products.map((product) => (
      <Table.Row key={product.id}>
        <Table.Cell>{getCellValue(product.sku)}</Table.Cell>
        <Table.Cell>{renderType(product.type)}</Table.Cell>
        <Table.Cell>{getCellValue(product.tn_id)}</Table.Cell>
        <Table.Cell>{getCellValue(product.siigo_id)}</Table.Cell>
        <Table.Cell>{getCellValue(product.message)}</Table.Cell>
        <Table.Cell>{getCellValue(product.created_at)}</Table.Cell>
      </Table.Row>
    ));
  };

  const renderCustomersHeader = () => (
    <Table.Head>
      <Table.Row backgroundColor="neutral-surface">
        <Table.Cell as="th" width="110px">
          IDENTIFICACION
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          TIPO
        </Table.Cell>
        <Table.Cell as="th" width="140px">
          NOMBRE
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          TN ID
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          SIIGO ID
        </Table.Cell>
        <Table.Cell as="th" width="240px">
          MENSAJE
        </Table.Cell>
        <Table.Cell as="th" width="150px">
          FECHA CREACION
        </Table.Cell>
      </Table.Row>
    </Table.Head>
  );

  const renderCustomersRows = () => {
    if (!customers.length) return renderEmptyRow(7);

    return customers.map((customer) => (
      <Table.Row key={customer.id}>
        <Table.Cell>
          {getCellValue(customer.customer_identification)}
        </Table.Cell>
        <Table.Cell>{renderType(customer.type)}</Table.Cell>
        <Table.Cell>{getCellValue(customer.customer_name)}</Table.Cell>
        <Table.Cell>{getCellValue(customer.tn_id)}</Table.Cell>
        <Table.Cell>{getCellValue(customer.siigo_id)}</Table.Cell>
        <Table.Cell>{getCellValue(customer.message)}</Table.Cell>
        <Table.Cell>{getCellValue(customer.created_at)}</Table.Cell>
      </Table.Row>
    ));
  };

  const renderOrdersHeader = () => (
    <Table.Head>
      <Table.Row backgroundColor="neutral-surface">
        <Table.Cell as="th" width="100px">
          NRO ORDEN
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          TIPO
        </Table.Cell>
        <Table.Cell as="th" width="130px">
          IDENTIFICACION CLI
        </Table.Cell>
        <Table.Cell as="th" width="80px">
          SIIGO ID
        </Table.Cell>
        <Table.Cell as="th" width="240px">
          MENSAJE
        </Table.Cell>
        <Table.Cell as="th" width="150px">
          FECHA CREACION
        </Table.Cell>
        <Table.Cell as="th" width="140px">
          ACCION
        </Table.Cell>
      </Table.Row>
    </Table.Head>
  );

  const renderOrdersRows = () => {
    if (!orders.length) return renderEmptyRow(7);

    return orders.map((order) => (
      <Table.Row key={order.id}>
        <Table.Cell>{getCellValue(order.tn_order_number)}</Table.Cell>
        <Table.Cell>{renderType(order.type)}</Table.Cell>
        <Table.Cell>{getCellValue(order.customer_identification)}</Table.Cell>
        <Table.Cell>{getCellValue(order.siigo_id)}</Table.Cell>
        <Table.Cell>{getCellValue(order.message)}</Table.Cell>
        <Table.Cell>{getCellValue(order.created_at)}</Table.Cell>
        <Table.Cell>{renderOrderRetryAction(order)}</Table.Cell>
      </Table.Row>
    ));
  };

  const renderTableHeader = () => {
    if (activeResource === 'orders') return renderOrdersHeader();
    if (activeResource === 'customers') return renderCustomersHeader();

    return renderProductsHeader();
  };

  const renderTableRows = () => {
    if (activeResource === 'orders') return renderOrdersRows();
    if (activeResource === 'customers') return renderCustomersRows();

    return renderProductsRows();
  };

  const renderMobileRows = () => {
    if (activeResource === 'orders') {
      if (!orders.length) return renderMobileEmptyRow();

      return orders.map((order) =>
        renderMobileRow(
          order.id,
          `Orden ${getCellValue(order.tn_order_number)}`,
          order.type,
          [
            {
              label: 'Identificacion cliente',
              value: order.customer_identification,
            },
            { label: 'Siigo ID', value: order.siigo_id },
            { label: 'Mensaje', value: order.message },
            { label: 'Fecha creacion', value: order.created_at },
          ],
          renderOrderRetryAction(order),
        ),
      );
    }

    if (activeResource === 'customers') {
      if (!customers.length) return renderMobileEmptyRow();

      return customers.map((customer) =>
        renderMobileRow(
          customer.id,
          `Cliente ${getCellValue(customer.customer_name)}`,
          customer.type,
          [
            {
              label: 'Identificacion',
              value: customer.customer_identification,
            },
            { label: 'TN ID', value: customer.tn_id },
            { label: 'Siigo ID', value: customer.siigo_id },
            { label: 'Mensaje', value: customer.message },
            { label: 'Fecha creacion', value: customer.created_at },
          ],
        ),
      );
    }

    if (!products.length) return renderMobileEmptyRow();

    return products.map((product) =>
      renderMobileRow(
        product.id,
        `SKU ${getCellValue(product.sku)}`,
        product.type,
        [
          { label: 'TN ID', value: product.tn_id },
          { label: 'Siigo ID', value: product.siigo_id },
          { label: 'Mensaje', value: product.message },
          { label: 'Fecha creacion', value: product.created_at },
        ],
      ),
    );
  };

  const renderTableFooter = () => (
    <DataTable.Footer
      itemCount={getItemCountText()}
      pagination={
        activePagination.last_page > 1
          ? {
              activePage: activePagination.current_page,
              pageCount: activePagination.last_page,
              onPageChange: handlePageChange,
            }
          : undefined
      }
    />
  );

  const renderDesktopContent = () => (
    <>
      <Box
        aria-label="Tabla de registros"
        maxWidth="100%"
        overflowX="auto"
        role="region"
        tabIndex={0}
        width="100%"
      >
        <Box minWidth={TABLE_MIN_WIDTHS[activeResource]}>
          <DataTable header={renderTableHeader()}>
            {renderTableRows()}
          </DataTable>
        </Box>
      </Box>
      {renderTableFooter()}
    </>
  );

  const renderMobileContent = () => (
    <>
      <DataList>{renderMobileRows()}</DataList>
      {renderTableFooter()}
    </>
  );

  const renderRefreshAction = () => (
    <Button
      appearance="neutral"
      disabled={isLoading}
      onClick={() => fetchLogs(activeResource)}
    >
      <Icon source={<RedoIcon />} color="currentColor" />
      Refrescar
    </Button>
  );

  const renderResourceActions = () => (
    <Box display="flex" gap="2" flexWrap="wrap">
      <Button
        appearance={activeResource === 'products' ? 'primary' : 'neutral'}
        onClick={() => viewResource('products')}
      >
        <Icon source={<BoxUnpackedIcon />} color="currentColor" />
        Productos
      </Button>
      <Button
        appearance={activeResource === 'orders' ? 'primary' : 'neutral'}
        onClick={() => viewResource('orders')}
      >
        <Icon source={<ShoppingCartIcon />} color="currentColor" />
        Ordenes
      </Button>
      <Button
        appearance={activeResource === 'customers' ? 'primary' : 'neutral'}
        onClick={() => viewResource('customers')}
      >
        <Icon source={<UserIcon />} color="currentColor" />
        Clientes
      </Button>
    </Box>
  );

  const actions = (
    <>
      <Box display={{ xs: 'none', md: 'flex' }} gap="2" flexWrap="wrap">
        {renderRefreshAction()}
        {renderResourceActions()}
      </Box>
      <Box display={{ xs: 'block', md: 'none' }}>{renderRefreshAction()}</Box>
    </>
  );

  return (
    <Page maxWidth="1200px">
      <Page.Header title="Registros" buttonStack={actions} />
      <Page.Body>
        <Card>
          <Card.Header title={getResourceTitle()} />
          <Card.Body>
            <Box display="flex" flexDirection="column" gap="4">
              <Box display={{ xs: 'flex', md: 'none' }} gap="2" flexWrap="wrap">
                {renderResourceActions()}
              </Box>
              <Box
                alignItems={{ xs: 'stretch', md: 'center' }}
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                flexWrap={{ xs: 'nowrap', md: 'wrap' }}
                gap="2"
              >
                <Box width={{ xs: '100%', md: '280px' }}>
                  <Input.Search
                    disabled={isLoading}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={getSearchPlaceholder()}
                    value={activeFilters.search}
                  />
                </Box>
                <Box width={{ xs: '100%', md: '180px' }}>
                  <Select
                    appearance="neutral"
                    disabled={isLoading}
                    id="logs-type-filter"
                    name="logs-type-filter"
                    onChange={handleTypeChange}
                  >
                    <Select.Option
                      label="Todos los tipos"
                      selected={activeFilters.type === 'ALL'}
                      value="ALL"
                    />
                    <Select.Option
                      label="Exito"
                      selected={activeFilters.type === 'EXITO'}
                      value="EXITO"
                    />
                    <Select.Option
                      label="Error"
                      selected={activeFilters.type === 'ERROR'}
                      value="ERROR"
                    />
                    <Select.Option
                      label="Info"
                      selected={activeFilters.type === 'INFO'}
                      value="INFO"
                    />
                  </Select>
                </Box>
                <Box width={{ xs: '100%', md: '180px' }}>
                  <Select
                    appearance="neutral"
                    disabled={isLoading}
                    id="logs-date-preset-filter"
                    name="logs-date-preset-filter"
                    onChange={handleDatePresetChange}
                  >
                    <Select.Option
                      label="Rango manual"
                      selected={activeFilters.datePreset === 'CUSTOM'}
                      value="CUSTOM"
                    />
                    <Select.Option
                      label="Hoy"
                      selected={activeFilters.datePreset === 'TODAY'}
                      value="TODAY"
                    />
                    <Select.Option
                      label="Ultimos 7 dias"
                      selected={activeFilters.datePreset === 'LAST_7_DAYS'}
                      value="LAST_7_DAYS"
                    />
                    <Select.Option
                      label="Ultimos 30 dias"
                      selected={activeFilters.datePreset === 'LAST_30_DAYS'}
                      value="LAST_30_DAYS"
                    />
                  </Select>
                </Box>
                <Box display="flex" gap="2" width={{ xs: '100%', md: 'auto' }}>
                  <Button
                    appearance="primary"
                    disabled={isLoading}
                    onClick={applyCurrentFilters}
                  >
                    Buscar
                  </Button>
                  <Button
                    appearance="neutral"
                    disabled={isLoading}
                    onClick={clearFilters}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Box>
              <Box
                alignItems={{ xs: 'stretch', md: 'center' }}
                display="flex"
                flexDirection={{ xs: 'column', md: 'row' }}
                gap="2"
              >
                <Box
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap="2"
                  width={{ xs: '100%', md: 'auto' }}
                >
                  <Text fontSize="caption">Desde</Text>
                  <Box width={{ xs: '100%', md: '150px' }}>
                    <Input
                      aria-label="Desde"
                      disabled={isLoading}
                      inputMode="numeric"
                      maxLength={10}
                      name="logs-date-from"
                      onChange={handleDateChange('dateFrom')}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="dd/mm/aaaa"
                      type="text"
                      value={activeFilters.dateFrom}
                    />
                  </Box>
                </Box>
                <Box
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  display="flex"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap="2"
                  width={{ xs: '100%', md: 'auto' }}
                >
                  <Text fontSize="caption">Hasta</Text>
                  <Box width={{ xs: '100%', md: '150px' }}>
                    <Input
                      aria-label="Hasta"
                      disabled={isLoading}
                      inputMode="numeric"
                      maxLength={10}
                      name="logs-date-to"
                      onChange={handleDateChange('dateTo')}
                      onKeyDown={handleSearchKeyDown}
                      placeholder="dd/mm/aaaa"
                      type="text"
                      value={activeFilters.dateTo}
                    />
                  </Box>
                </Box>
              </Box>
              <Box display={{ xs: 'none', md: 'block' }} minWidth="0">
                {renderDesktopContent()}
              </Box>
              <Box display={{ xs: 'block', md: 'none' }} minWidth="0">
                {renderMobileContent()}
              </Box>
            </Box>
          </Card.Body>
        </Card>
        {renderRetryModal()}
      </Page.Body>
    </Page>
  );
};

export default LogsPage;
